import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getFirstName } from '../../utils/formatters';
import VideoPlayer from '../video/VideoPlayer';
import EnhancedWorksheetDisplay from '../worksheet/EnhancedWorksheetDisplay';
import FullAnalysisModal from './FullAnalysisModal';
import CompareAnswersModal from './CompareAnswersModal';

const ViewSyllabusModal = ({
  child,
  selectedSubject: preSelectedSubject,
  onClose,
  currentUser,
  allowBookManagement = true,
  allowAssignment = true,
  renderAsPage = false,
  onOpenWorksheetGenerator = null,
  onOpenNotesGenerator = null,
  onOpenVideoGenerator = null,
  onOpenLessonKit = null
}) => {
  const [syllabus, setSyllabus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSubject, setSelectedSubject] = useState(preSelectedSubject || '');
  const [subjects, setSubjects] = useState([]);
  
  // Book management states
  const [showBookManagement, setShowBookManagement] = useState(false);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState(''); // For coaching teachers
  const [boards, setBoards] = useState([]);
  const [schoolCurriculumAvailable, setSchoolCurriculumAvailable] = useState(false);
  const [schoolCurriculumOnboarded, setSchoolCurriculumOnboarded] = useState(false);
  const [didApplyDefaults, setDidApplyDefaults] = useState(false);
  // Default/required/locked policy returned by /books/available — single
  // source of truth for which books to pre-check, which can't be unchecked,
  // and whether the whole selection is read-only.
  const [selectionPolicy, setSelectionPolicy] = useState({
    defaultBooks: [],
    requiredBooks: [],
    locked: false,
  });
  const [chapterMaterialAvailability, setChapterMaterialAvailability] = useState({});
  // Which chapters/topics already have an assigned worksheet/notes/video for
  // this child (or batch) — drives the "Assigned" tick mark. Keyed by
  // getChapterAssignmentKey/getTopicAssignmentKey (subject+chapter, no
  // book_title — an assignment isn't tied to a specific book edition).
  const [chapterAssignmentStatus, setChapterAssignmentStatus] = useState({});
  const [expandedChapters, setExpandedChapters] = useState({});
  // Which selected book's syllabus is currently shown. Selected books can each
  // carry many chapters/topics; rendering them all stacked makes the panel a
  // very long scroll, so we show one book at a time behind a pill selector.
  const [activeBookIndex, setActiveBookIndex] = useState(0);
  // Tracks which chapter/topic row's Create or Assign dropdown is open —
  // { key: '<bookTitle>::<chapter>[::<topic>]', kind: 'create' | 'assign' }
  const [openMenu, setOpenMenu] = useState(null);
  // "Full Analysis" & "Compare" for a completed worksheet — reuse the same
  // FullAnalysisModal / CompareAnswersModal dialogs (and /tasks/{id}/comparison
  // endpoint) as the coaching task cards, for a consistent experience.
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [fullAnalysisData, setFullAnalysisData] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [compareData, setCompareData] = useState(null);

  // Internal material viewer states
  const [internalVideoModal, setInternalVideoModal] = useState(null); // video object
  const [internalNotesModal, setInternalNotesModal] = useState(null); // { html_content, title }
  const [internalWorksheetModal, setInternalWorksheetModal] = useState(null); // { worksheet, childId }
  const [assigningWorksheet, setAssigningWorksheet] = useState(false);
  const [wsAssignmentData, setWsAssignmentData] = useState({
    title: '', description: '', due_date: '', hint_enabled: true, target_type: 'child'
  });
  // Chapter/topic the currently-open internal material modal was launched
  // from, so the assign POST bodies below can tag the resulting
  // Task/note_assignment/video_assignment with it (see backend TaskAssign /
  // VideoAssignRequest chapter/topic fields).
  const [assigningChapterContext, setAssigningChapterContext] = useState({ bookTitle: null, chapter: null, topic: null });
  const [wsAssignmentTargets, setWsAssignmentTargets] = useState({ students: [], batches: [], children: [] });
  const [wsSelectedStudents, setWsSelectedStudents] = useState([]);
  const [wsSelectedBatches, setWsSelectedBatches] = useState([]);
  const [wsLoadingTargets, setWsLoadingTargets] = useState(false);

  const normalizeBookTitle = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  
  const curriculumAvailable = (child?.curriculum_available || '').trim().toLowerCase() === 'yes';
  const hasSchoolReference = Boolean(child?.school_id || child?.school_name);
  const usesSchoolCurriculum = (curriculumAvailable || schoolCurriculumAvailable) && hasSchoolReference;
  // Lock comes from the backend policy now — the backend knows whether the
  // user is a school teacher with an onboarded school (truly locked) or a
  // parent (never locked, just has required books).
  const isSelectionLocked = selectionPolicy.locked;
  const requiredBookSet = useMemo(
    () => new Set((selectionPolicy.requiredBooks || []).map(normalizeBookTitle)),
    [selectionPolicy.requiredBooks]
  );

  useEffect(() => {
    if (child) {
      loadSyllabus();
      loadSelectedBooks();
      loadSchoolCurriculumStatus();
    }
    // Include grade/board/school so an in-place child edit (Child Management
    // page mutates the existing child object) triggers a reload instead of
    // stranding the modal on stale state.
  }, [child?.id, child?.grade, child?.board, child?.school_id, child?.school_name, child?.curriculum_available]);

  // Reset the "defaults already applied" guard whenever the (child, grade,
  // subject) tuple changes — a new tuple deserves a fresh defaults pass.
  useEffect(() => {
    setDidApplyDefaults(false);
    // Also clear the in-memory selection so a stale Math-grade-5 list never
    // bleeds into English-grade-5.  loadSelectedBooks below repopulates from
    // the per-(grade, subject) backend map.
    setSelectedBooks([]);
    if (child && selectedSubject) {
      loadSelectedBooks();
    }
  }, [child?.id, child?.grade, selectedSubject]);

  const loadBoards = async () => {
    try {
      const response = await axios.get('/qb/syllabus/boards');
      setBoards(response.data.boards);
    } catch (error) {
      console.error('Error loading boards:', error);
    }
  };

  const loadSelectedBooks = async () => {
    try {
      // For batch context (coaching teachers), load from teacher preferences
      if (child.id?.startsWith('batch_')) {
        const response = await axios.get('/coaching/book-preferences');
        const preferences = response.data.book_preferences || {};
        // Coaching teachers key by the BATCH's subject (the modal's subject
        // toggle is for browsing, not the persisted scope).
        const preferenceKey = `${child.grade}_${child.subject}`;
        setSelectedBooks(preferences[preferenceKey] || []);
        return;
      }
      
      // Regular child - per-(grade, subject) selection.
      const subjectForLookup = selectedSubject || child.subject;
      const params = {};
      if (child.grade != null) params.grade = child.grade;
      if (subjectForLookup) params.subject = subjectForLookup;
      const response = await axios.get(`/children/${child.id}/books/selected`, { params });
      setSelectedBooks(response.data.selected_books || []);
    } catch (error) {
      console.error('Error loading selected books:', error);
    }
  };

  const loadSchoolCurriculumStatus = async () => {
    if (!hasSchoolReference) {
      setSchoolCurriculumAvailable(false);
      setSchoolCurriculumOnboarded(false);
      return;
    }

    try {
      let schoolRecord = null;

      if (child.school_id) {
        const response = await axios.get(`/schools/${child.school_id}`);
        schoolRecord = response.data;
      } else if (child.school_name) {
        const response = await axios.get('/schools/search', {
          params: { query: child.school_name, limit: 5 }
        });
        const normalizedChildName = child.school_name.trim().toLowerCase();
        schoolRecord = response.data.schools?.find(
          (school) => (school.name || '').trim().toLowerCase() === normalizedChildName
        ) || response.data.schools?.[0];
      }

      setSchoolCurriculumAvailable((schoolRecord?.curriculum_available || '').trim().toLowerCase() === 'yes');
      setSchoolCurriculumOnboarded((schoolRecord?.curriculum_onboarded || '').trim().toLowerCase() === 'yes');
    } catch (error) {
      console.error('Error loading school curriculum status:', error);
      setSchoolCurriculumAvailable(false);
      setSchoolCurriculumOnboarded(false);
    }
  };

  const loadAvailableBooks = async (boardOverride = null) => {
    try {
      setLoadingBooks(true);
      
      // For coaching teacher with batch context, use curriculum endpoint (no child dependency)
      if (['coaching_teacher', 'school_teacher'].includes(currentUser?.role) && child.id?.startsWith('batch_')) {
        // Use the new curriculum endpoint that doesn't need child_id
        const grade = child.grade;
        // Use batch subject, not selectedSubject (which can be changed in dropdown)
        const subject = child.subject;
        
        if (!subject) {
          console.error('No subject in batch context');
          setAvailableBooks([]);
          return;
        }
        
        const response = await axios.get('/books/by-curriculum', {
          params: {
            grade: grade,
            subject: subject
            // No board param = fetch from all boards
          }
        });
        
        const allBooks = response.data.books || [];
        
        // Remove duplicate books across boards by ISBN
        const uniqueBooks = [];
        const seenISBNs = new Set();
        
        for (const book of allBooks) {
          const isbn = book.isbn_13 || book.isbn_10;
          
          // If book has no ISBN or ISBN not seen before, include it
          if (!isbn || !seenISBNs.has(isbn)) {
            uniqueBooks.push(book);
            if (isbn) {
              seenISBNs.add(isbn);
            }
          }
        }
        
        console.log(`[ViewSyllabus] Loaded ${allBooks.length} books, ${uniqueBooks.length} unique after ISBN deduplication`);
        setAvailableBooks(uniqueBooks);
        setSelectionPolicy({
          defaultBooks: response.data.default_books || [],
          requiredBooks: response.data.required_books || [],
          locked: Boolean(response.data.locked),
        });
      } else {
        // Regular parent flow
        // Keep Manage Books aligned with syllabus cards shown below the modal.
        {/*const subjectSyllabus = selectedSubject ? syllabus?.[selectedSubject] : null;
        const syllabusBooks = subjectSyllabus?.books || [];

        if (syllabusBooks.length > 0) {
          const mappedBooks = syllabusBooks.map((book, index) => ({
            book_id: book.id || '',
            edsupp_book_id: book.edsupp_book_id || '',
            book_title: book.title || book.book_title || `Book ${index + 1}`,
            publisher: book.publisher || 'Unknown',
            thumbnail_url: book.thumbnail_url || '',
            subject: selectedSubject || book.subject || '',
            board: child?.board || '',
            grade: `Grade ${child?.grade || ''}`,
            chapter_count: Object.keys(book.chapters || {}).length,
          }));
          setAvailableBooks(mappedBooks);
          return;
        }*/}

        // Parent / child-scoped path
        const board = boardOverride || selectedBoard;
        let url = `/children/${child.id}/books/available`;
        const params = new URLSearchParams();
        if (board && ['coaching_teacher', 'school_teacher'].includes(currentUser?.role)) {
          params.append('board', board);
        }
        if (selectedSubject) {
          params.append('subject', selectedSubject);
        }
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
        const response = await axios.get(url);
        const allBooks = response.data.books || [];
        
        // Filter books by selected subject
        const filteredBooks = selectedSubject
          ? allBooks.filter(book => book.subject === selectedSubject)
          : allBooks;
        
        setAvailableBooks(filteredBooks);
        setSelectionPolicy({
          defaultBooks: response.data.default_books || [],
          requiredBooks: response.data.required_books || [],
          locked: Boolean(response.data.locked),
        });
      }
    } catch (error) {
      console.error('Error loading available books:', error);
    } finally {
      setLoadingBooks(false);
    }
  };

  const handleToggleBookSelection = (bookTitle) => {
    if (isSelectionLocked) {
      return;
    }
    const normalizedTitle = normalizeBookTitle(bookTitle);
    // Required books (parent + school curriculum_onboarded) must stay checked.
    if (requiredBookSet.has(normalizedTitle)) {
      return;
    }
    setSelectedBooks(prev => {
      const normalizedSelections = prev.map(normalizeBookTitle);
      if (normalizedSelections.includes(normalizedTitle)) {
        return prev.filter((b) => normalizeBookTitle(b) !== normalizedTitle);
      }
      return [...prev, bookTitle];
    });
  };

  const handleSaveBookSelection = async () => {
    try {
      if (isSelectionLocked) {
        return;
      }
      // For batch context (coaching teachers), save to teacher preferences
      if (child.id?.startsWith('batch_')) {
        await axios.put('/coaching/book-preferences', {
          grade: child.grade,
          subject: child.subject,
          selected_books: selectedBooks
        });
        alert('Book preferences saved successfully!');
        setShowBookManagement(false);
        // Reload syllabus to reflect changes
        loadSyllabus();
        return;
      }
      
      // Regular child - save to child profile, scoped to (grade, subject) so
      // switching subject afterwards doesn't drop sibling selections.
      await axios.put(`/children/${child.id}/books/selected`, {
        grade: child.grade,
        subject: selectedSubject || child.subject,
        selected_books: selectedBooks,
      });
      alert('Book selection saved successfully!');
      setShowBookManagement(false);
      // Reload syllabus to reflect changes
      loadSyllabus();
    } catch (error) {
      console.error('Error saving book selection:', error);
      alert('Failed to save book selection');
    }
  };

  const handleBoardChange = (board) => {
    setSelectedBoard(board);
    loadAvailableBooks(board);
  };

  const handleToggleBookManagement = () => {
    setShowBookManagement(!showBookManagement);
    if (!showBookManagement) {
      // Load books when opening management section
      loadAvailableBooks();
    }
  };
  
  // Reload books when subject or grade changes
  useEffect(() => {
    if (showBookManagement && selectedSubject) {
      loadAvailableBooks();
    }
  }, [selectedSubject, child?.grade, child?.board]);

  // Apply server-computed defaults (or required-book overrides) once per
  // (grade, subject) when the available list lands.  All role-specific rules
  // live in the backend's policy response, so the modal stays dumb here.
  useEffect(() => {
    if (!availableBooks.length) {
      return;
    }
    const requiredTitles = selectionPolicy.requiredBooks || [];
    const defaultTitles = selectionPolicy.defaultBooks || [];

    // Locked + required → mirror the server-mandated selection exactly.
    if (isSelectionLocked && requiredTitles.length) {
      setSelectedBooks(requiredTitles);
      setDidApplyDefaults(true);
      return;
    }

    if (didApplyDefaults) {
      // Required books always win — keep them checked even after user edits.
      if (requiredTitles.length) {
        const currentNormalized = new Set(selectedBooks.map(normalizeBookTitle));
        const missing = requiredTitles.filter(
          (t) => !currentNormalized.has(normalizeBookTitle(t))
        );
        if (missing.length) {
          setSelectedBooks((prev) => [...prev, ...missing]);
        }
      }
      return;
    }

    // First pass for this (grade, subject): pre-check defaults if the user
    // has no saved selection yet, and ensure required books are present.
    const seed = selectedBooks.length
      ? Array.from(new Set([...selectedBooks, ...requiredTitles]))
      : Array.from(new Set([...defaultTitles, ...requiredTitles]));
    if (seed.length) {
      setSelectedBooks(seed);
    }
    setDidApplyDefaults(true);
  }, [
    availableBooks,
    selectionPolicy.defaultBooks,
    selectionPolicy.requiredBooks,
    isSelectionLocked,
    didApplyDefaults,
  ]);
  
  // Build a teacher's syllabus view for one (grade, subject) batch.  Chapter
  // structure comes from the Master syllabus (canonical), but books are
  // sourced from /books/by-curriculum (all boards) so that a teacher who
  // selected a CBSE book actually sees it here — the legacy path silently
  // dropped non-Master picks because /syllabus only returned Master books.
  const loadTeacherBatchSyllabus = useCallback(async (subjectToLoad) => {
    if (!subjectToLoad) return;
    const grade = child.grade;
    try {
      const [syllabusResp, booksResp, prefResp] = await Promise.all([
        axios.get(`/syllabus/${subjectToLoad}`, { params: { grade, board: 'Master' } }),
        axios.get('/books/by-curriculum', { params: { grade, subject: subjectToLoad } }),
        axios.get('/coaching/book-preferences').catch(() => ({ data: { book_preferences: {} } })),
      ]);

      const chaptersList = syllabusResp.data?.chapters || [];
      const allBoardBooks = booksResp.data?.books || [];
      const preferences = prefResp.data?.book_preferences || {};
      const teacherPreferences = preferences[`${grade}_${subjectToLoad}`] || [];

      const masterChaptersObj = {};
      chaptersList.forEach((ch) => {
        masterChaptersObj[ch.chapter] = ch.topics;
      });

      const normalizedPreferences = new Set(teacherPreferences.map(normalizeBookTitle));
      const booksToDisplay = teacherPreferences.length
        ? allBoardBooks.filter((book) => normalizedPreferences.has(normalizeBookTitle(book.book_title)))
        : allBoardBooks;

      const normalizeChapterMap = (chapters) => {
        if (!chapters) return masterChaptersObj;
        if (Array.isArray(chapters)) {
          return chapters.reduce((acc, chapter) => {
            const chapterName = chapter?.chapter || chapter?.name || chapter?.title;
            if (chapterName) acc[chapterName] = chapter?.topics || [];
            return acc;
          }, {});
        }
        return Object.keys(chapters).length > 0 ? chapters : masterChaptersObj;
      };

      const books = booksToDisplay.map((book) => ({
        title: book.book_title,
        publisher: book.publisher,
        authors: book.authors,
        isbn_13: book.isbn_13,
        thumbnail_url: book.thumbnail_url,
        chapters: normalizeChapterMap(book.chapters || book.details),
      }));

      return { books };
    } catch (error) {
      console.error(`Error loading teacher syllabus for ${subjectToLoad}:`, error);
      return { books: [] };
    }
  }, [child?.grade]);

  // Teacher/batch equivalent of the material-availability + assignment-status
  // blocks the parent path gets from GET /children/{child_id}/syllabus (see
  // backend get_batch_chapter_status) — a batch has no single child to key
  // materials off of, so this is a separate call.
  const loadBatchChapterStatus = useCallback(async (subjectToLoad) => {
    const batchId = child?.id?.startsWith('batch_') ? child.id.slice('batch_'.length) : null;
    if (!batchId || !subjectToLoad) {
      setChapterMaterialAvailability({});
      setChapterAssignmentStatus({});
      return;
    }
    try {
      const response = await axios.get(`/coaching/batch/${batchId}/chapter-status`, {
        params: { subject: subjectToLoad },
      });
      setChapterMaterialAvailability(response.data?.chapter_material_availability || {});
      setChapterAssignmentStatus(response.data?.chapter_assignment_status || {});
    } catch (error) {
      console.error(`Error loading batch chapter status for ${subjectToLoad}:`, error);
      setChapterMaterialAvailability({});
      setChapterAssignmentStatus({});
    }
  }, [child?.id]);

  // Reload syllabus when subject or grade changes for coaching teachers.
  useEffect(() => {
    if (!['coaching_teacher', 'school_teacher'].includes(currentUser?.role)) return;
    if (!child?.id?.startsWith('batch_')) return;
    if (!selectedSubject) return;
    let cancelled = false;
    (async () => {
      const subjectData = await loadTeacherBatchSyllabus(selectedSubject);
      if (cancelled || !subjectData) return;
      setSyllabus((prev) => ({ ...prev, [selectedSubject]: subjectData }));
      await loadBatchChapterStatus(selectedSubject);
    })();
    return () => { cancelled = true; };
  }, [selectedSubject, child?.grade, currentUser?.role, loadTeacherBatchSyllabus, loadBatchChapterStatus]);

  const loadSyllabus = async () => {
    try {
      setLoading(true);
      
      const isBatchContext =
        ['coaching_teacher', 'school_teacher'].includes(currentUser?.role)
        && child.id?.startsWith('batch_');

      if (isBatchContext) {
        const subjectToSelect = child.subject || 'English';
        setSelectedSubject(subjectToSelect);
        setSubjects([subjectToSelect]);
        const subjectData = await loadTeacherBatchSyllabus(subjectToSelect);
        setSyllabus(subjectData ? { [subjectToSelect]: subjectData } : {});
        await loadBatchChapterStatus(subjectToSelect);
      } else {
        // Regular parent flow - load from child endpoint
        const response = await axios.get(`/children/${child.id}/syllabus`);
        setSyllabus(response.data.syllabus);
        setChapterMaterialAvailability(response.data.chapter_material_availability || {});
        setChapterAssignmentStatus(response.data.chapter_assignment_status || {});
        setSubjects(response.data.subjects || []);
        if (!selectedSubject && response.data.subjects && response.data.subjects.length > 0) {
          setSelectedSubject(response.data.subjects[0]);
        }
      }
    } catch (error) {
      console.error('Error loading syllabus:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSubjectSyllabus = () => {
    if (!syllabus || !selectedSubject) return null;
    return syllabus[selectedSubject] || null;
  };

  useEffect(() => {
    const subjectSyllabus = getSubjectSyllabus();
    // A new subject/syllabus deserves a fresh view — jump back to the first book.
    setActiveBookIndex(0);
    if (!subjectSyllabus?.books?.length) {
      setExpandedChapters({});
      return;
    }

    const nextExpanded = {};
    subjectSyllabus.books.forEach((book, bookIndex) => {
      Object.keys(book.chapters || {}).forEach((chapterName) => {
        nextExpanded[`${book.title || `book-${bookIndex}`}::${chapterName}`] = false;
      });
    });
    setExpandedChapters(nextExpanded);
  }, [syllabus, selectedSubject]);

  const normalizedSelectedBooks = new Set(selectedBooks.map(normalizeBookTitle));

  // Books for the active subject, plus a clamped active index so the pill
  // selector never points past the end when the book list changes.
  const subjectBooks = getSubjectSyllabus()?.books || [];
  const activeBookIdx = subjectBooks.length
    ? Math.min(activeBookIndex, subjectBooks.length - 1)
    : 0;

  const getChapterMaterialKey = (subject, bookTitle, chapter) =>
    [subject, bookTitle, chapter]
      .map((value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' '))
      .join('||');

  const getTopicMaterialKey = (subject, bookTitle, chapter, topic) =>
    [subject, bookTitle, chapter, topic]
      .map((value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' '))
      .join('||');

  const getMaterialAvailability = ({ subject, bookTitle, chapter, topic = '' }) => {
    const key = topic
      ? getTopicMaterialKey(subject, bookTitle, chapter, topic)
      : getChapterMaterialKey(subject, bookTitle, chapter);
    return chapterMaterialAvailability?.[key] || { has_materials: false, count: 0 };
  };

  // Assignment-status keys — same shape as getChapterMaterialKey/
  // getTopicMaterialKey above (subject, book_title, chapter[, topic]).
  // book_title MUST be included: chapter names aren't unique across a
  // subject's different books (e.g. every grade-6 Science book has a
  // "Chapter 1"), so omitting it would collide two unrelated chapters'
  // assignment status onto the same tick mark.
  const getChapterAssignmentKey = (subject, bookTitle, chapter) =>
    [subject, bookTitle, chapter]
      .map((value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' '))
      .join('||');

  const getTopicAssignmentKey = (subject, bookTitle, chapter, topic) =>
    [subject, bookTitle, chapter, topic]
      .map((value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' '))
      .join('||');

  // Shape: { assigned, status: 'pending'|'completed', types: [...],
  //          worksheet: {status, task_id, score_percent} | undefined,
  //          notes: {status, assignment_id, score_percent} | undefined,
  //          video: {status, assignment_id} | undefined }
  const getAssignmentStatus = ({ subject, bookTitle, chapter, topic = '' }) => {
    const key = topic
      ? getTopicAssignmentKey(subject, bookTitle, chapter, topic)
      : getChapterAssignmentKey(subject, bookTitle, chapter);
    return chapterAssignmentStatus?.[key] || { assigned: false, status: 'not_assigned', types: [] };
  };

  // The chapter row's tick mark should only light up when EVERY topic under
  // it is covered — a single topic being created/assigned shouldn't promote
  // the whole chapter to "assigned"/"completed" (that used to happen
  // because topic-less assignments register directly against the chapter
  // key). Aggregates over each topic's own status, and ORs in a direct
  // chapter-level registration for genuinely chapter-wide content (e.g. one
  // worksheet deliberately spanning every topic, created via the chapter's
  // own Create button with no single topic isolated).
  const getChapterAggregateStatus = (subject, bookTitle, chapter, topicsForChapter) => {
    const directInfo = getAssignmentStatus({ subject, bookTitle, chapter });
    const topicList = (Array.isArray(topicsForChapter) ? topicsForChapter : [topicsForChapter]).filter(Boolean);
    if (topicList.length === 0) return directInfo;

    const topicInfos = topicList.map((topic) => getAssignmentStatus({ subject, bookTitle, chapter, topic }));
    const allTopicsCompleted = topicInfos.every((info) => info.status === 'completed');
    const allTopicsAssigned = topicInfos.every((info) => info.assigned);
    const combinedTypes = () => Array.from(new Set(topicInfos.flatMap((info) => info.types)));

    if (directInfo.status === 'completed' || allTopicsCompleted) {
      return directInfo.status === 'completed'
        ? directInfo
        : { assigned: true, status: 'completed', types: combinedTypes() };
    }
    if (directInfo.assigned || allTopicsAssigned) {
      return directInfo.assigned
        ? directInfo
        : { assigned: true, status: 'pending', types: combinedTypes() };
    }
    return { assigned: false, status: 'not_assigned', types: [] };
  };

  // Optimistic local update so the "Assigned" tick mark appears immediately
  // after a successful assign, without waiting for a full syllabus reload —
  // always starts "pending" since the child hasn't had a chance to complete
  // it yet; a later reload picks up the real completed/score state.
  const markChapterAssigned = (subject, bookTitle, chapter, topic, contentType) => {
    if (!chapter) return;
    const key = topic
      ? getTopicAssignmentKey(subject, bookTitle, chapter, topic)
      : getChapterAssignmentKey(subject, bookTitle, chapter);
    setChapterAssignmentStatus((prev) => {
      const existing = prev[key] || { assigned: true, status: 'pending', types: [] };
      if (existing.types.includes(contentType) && existing[contentType]) return prev;
      return {
        ...prev,
        [key]: {
          ...existing,
          assigned: true,
          status: existing.status === 'completed' ? 'completed' : 'pending',
          types: existing.types.includes(contentType) ? existing.types : [...existing.types, contentType],
          [contentType]: existing[contentType] || { status: 'pending' },
        },
      };
    });
  };

  // Three-state color scheme: not assigned (default) / assigned-pending
  // (amber) / completed (green).
  const getAssignmentCardClass = (info) => {
    if (info.status === 'completed') return 'bg-green-50 border-green-300';
    if (info.assigned) return 'bg-amber-50 border-amber-300';
    return 'bg-white border-gray-200';
  };

  const renderAssignmentBadge = (info) => {
    if (info.status === 'completed') {
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700"
          title={`Completed: ${info.types.join(', ')}`}
        >
          ✅ Completed
        </span>
      );
    }
    if (info.assigned) {
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700"
          title={`Assigned: ${info.types.join(', ')}`}
        >
          🕐 Assigned
        </span>
      );
    }
    return null;
  };

  // Score % + "Full Analysis"/"Compare" buttons for a completed worksheet —
  // the only content type with a real score and a detailed question-by-question
  // view. Notes/video just show the "Completed" badge.
  const renderScoreAndAnalysis = (info) => {
    const worksheetInfo = info.worksheet;
    if (!worksheetInfo || worksheetInfo.status !== 'completed') return null;
    return (
      <span className="inline-flex items-center gap-1">
        {worksheetInfo.score_percent != null && (
          <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">
            {worksheetInfo.score_percent}%
          </span>
        )}
        <button
          type="button"
          onClick={() => handleViewFullAnalysis(worksheetInfo.task_id)}
          disabled={analysisLoading}
          className="inline-flex items-center gap-1 rounded-md bg-indigo-100 px-2 py-1 text-[10px] sm:text-xs font-semibold hover:bg-indigo-200 disabled:opacity-60 whitespace-nowrap"
          title="View detailed learning analysis"
        >
          📈 Full Analysis
        </button>
        <button
          type="button"
          onClick={() => handleCompareAnswers(worksheetInfo.task_id)}
          disabled={analysisLoading}
          className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-[10px] sm:text-xs font-semibold hover:bg-blue-200 disabled:opacity-60 whitespace-nowrap"
          title="Compare answers"
        >
          📊 Compare
        </button>
      </span>
    );
  };

  // The three content types that can actually be assigned (as opposed to
  // url/pdf, which are just plain reference links) — consolidated into the
  // "Assign" dropdown instead of appearing as their own chips when
  // allowAssignment is true (see renderMaterialLinks below).
  const assignableMaterialTypes = [
    { type: 'worksheet', label: 'Sheet', icon: '📝' },
    { type: 'html', label: 'Notes', icon: '📄' },
    { type: 'video', label: 'Video', icon: '▶' },
  ];

  const getAssignableMaterials = ({ subject, bookTitle, chapter, topic = '' }) => {
    const availability = getMaterialAvailability({ subject, bookTitle, chapter, topic });
    return assignableMaterialTypes
      .map((cfg) => ({ ...cfg, count: availability?.by_type?.[cfg.type] || 0 }))
      .filter((cfg) => cfg.count > 0);
  };


  const materialButtonConfig = [
    { type: 'url', label: 'URL', className: 'bg-sky-100 hover:bg-sky-200', icon: '🔗' },
    { type: 'video', label: 'Video', className: 'bg-red-100 hover:bg-red-200', icon: '▶' },
    { type: 'html', label: 'Notes', className: 'bg-amber-100 hover:bg-amber-200', icon: '📄' },
    { type: 'pdf', label: 'PDF', className: 'bg-emerald-100 hover:bg-emerald-200', icon: '📑' },
    { type: 'worksheet', label: 'Sheet', className: 'bg-blue-100 hover:bg-blue-200', icon: '📝' },
  ];

  const loadWsAssignmentTargets = async () => {
    const role = currentUser?.role;
    setWsLoadingTargets(true);
    try {
      if (['coaching_teacher', 'school_teacher'].includes(role)) {
        const response = await axios.get('/coaching/assignment-targets');
        setWsAssignmentTargets(response.data);
      } else if (role === 'parent') {
        const response = await axios.get('/children');
        const children = response.data.children || [];
        setWsAssignmentTargets({ students: [], batches: [], children });
        if (child?.id) setWsSelectedStudents([child.id]);
      }
    } catch (e) {
      console.error('Error loading worksheet assignment targets:', e);
    } finally {
      setWsLoadingTargets(false);
    }
  };

  const openInternalMaterial = useCallback(async (url, context = {}) => {
    // URL format: /internal/{ref_type}/{ref_id}
    const match = url.match(/^\/internal\/(video|note|worksheet)\/(.+)$/);
    if (!match) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    const [, refType, refId] = match;
    // A chapter-level assignment (no specific topic) should keep the task
    // title as the chapter name rather than merging every topic in the
    // chapter together. Topic-level assignments fall back to the material's
    // own naming as before.
    const contextChapter = (context.chapter || '').trim();
    const contextTopic = (context.topic || '').trim();
    const isChapterLevel = Boolean(contextChapter) && !contextTopic;
    try {
      const response = await axios.get(`/internal-material/${refType}/${refId}`);
      const content = response.data.content;
      if (refType === 'video') {
        const videoTitle = isChapterLevel
          ? contextChapter
          : (content.title || (content.topic ? String(content.topic) : '') || 'Video');
        setWsAssignmentData({ title: videoTitle, description: '', due_date: '', hint_enabled: true, target_type: 'child' });
        setWsSelectedStudents([]);
        setWsSelectedBatches([]);
        setInternalVideoModal(content);
        if (allowAssignment) loadWsAssignmentTargets();
      } else if (refType === 'note') {
        const noteTitle = isChapterLevel
          ? contextChapter
          : ((content.topics || []).join(', ') || content.subject || 'Notes');
        setWsAssignmentData({ title: noteTitle, description: '', due_date: '', hint_enabled: true, target_type: 'child' });
        setWsSelectedStudents([]);
        setWsSelectedBatches([]);
        setInternalNotesModal({ html_content: content.html_content, title: noteTitle, id: content.id, subject: content.subject });
        if (allowAssignment) loadWsAssignmentTargets();
      } else if (refType === 'worksheet') {
        if (!allowAssignment) return;
        // Worksheet already titles a chapter assignment correctly via its
        // saved_title; keep that priority and only fall back to the chapter
        // name (instead of merged topics) for a chapter-level assignment.
        const wsTitle = content.saved_title
          || (isChapterLevel ? contextChapter : (content.topics || []).join(', '))
          || content.subject
          || 'Worksheet';
        setWsAssignmentData({ title: wsTitle, description: '', due_date: '', hint_enabled: true, target_type: 'child' });
        setWsSelectedStudents([]);
        setWsSelectedBatches([]);
        setInternalWorksheetModal({ worksheet: content, childId: child?.id });
        loadWsAssignmentTargets();
      }
    } catch (error) {
      console.error('Error loading internal material:', error);
      alert('Could not load this material. Please try again.');
    }
  }, [child?.id, currentUser?.role]);

  const handleOpenMaterial = async ({ subject, bookTitle, chapter, topic = '', materialType }) => {
    if (!child?.id) {
      return;
    }
    const isBatchContext = child.id.startsWith('batch_');
    setAssigningChapterContext({ bookTitle: bookTitle || null, chapter: chapter || null, topic: topic || null });
    const availability = getMaterialAvailability({ subject, bookTitle, chapter, topic });
    const quickUrl = availability?.sample_urls?.[materialType];
    if (quickUrl) {
      if (quickUrl.startsWith('/internal/')) {
        await openInternalMaterial(quickUrl, { chapter, topic });
      } else {
        window.open(quickUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    try {
      const params = {
        subject,
        chapter,
        topic: topic || undefined,
        book_title: bookTitle,
        material_type: materialType,
      };
      const response = isBatchContext
        ? await axios.get(`/coaching/batch/${child.id.slice('batch_'.length)}/study-materials`, { params })
        : await axios.get(`/children/${child.id}/study-materials`, { params });
      const firstMaterial = (response.data.materials || [])[0];
      if (firstMaterial?.url) {
        if (firstMaterial.url.startsWith('/internal/')) {
          await openInternalMaterial(firstMaterial.url, { chapter, topic });
        } else {
          window.open(firstMaterial.url, '_blank', 'noopener,noreferrer');
        }
      }
    } catch (error) {
      console.error('Error opening study material:', error);
    }
  };

  const handleAssignInternalWorksheet = async () => {
    if (!internalWorksheetModal?.worksheet?.id) return;
    const role = currentUser?.role;
    const targetIds = wsAssignmentData.target_type === 'batch' ? wsSelectedBatches : wsSelectedStudents;
    if (!targetIds.length) {
      alert('Please select at least one recipient before assigning.');
      return;
    }
    if (!wsAssignmentData.title.trim()) {
      alert('Please enter a task title.');
      return;
    }
    setAssigningWorksheet(true);
    const ws = internalWorksheetModal.worksheet;
    try {
      await axios.post(`/tasks/assign/${ws.id}`, {
        target_type: wsAssignmentData.target_type,
        target_ids: targetIds,
        title: wsAssignmentData.title.trim(),
        description: wsAssignmentData.description || undefined,
        due_date: wsAssignmentData.due_date || undefined,
        hint_enabled: wsAssignmentData.hint_enabled,
        book_title: assigningChapterContext.bookTitle || undefined,
        chapter: assigningChapterContext.chapter || undefined,
        topic: assigningChapterContext.topic || undefined,
      });
      markChapterAssigned(selectedSubject, assigningChapterContext.bookTitle, assigningChapterContext.chapter, assigningChapterContext.topic, 'worksheet');
      alert('Worksheet assigned successfully!');
      setInternalWorksheetModal(null);
    } catch (error) {
      console.error('Error assigning worksheet:', error);
      alert(error?.response?.data?.detail || 'Failed to assign worksheet. Please try again.');
    } finally {
      setAssigningWorksheet(false);
    }
  };

  const handleAssignNote = async () => {
    if (!internalNotesModal?.id) return;
    const targetIds = wsAssignmentData.target_type === 'batch' ? wsSelectedBatches : wsSelectedStudents;
    if (!targetIds.length) { alert('Please select at least one recipient before assigning.'); return; }
    if (!wsAssignmentData.title.trim()) { alert('Please enter a task title.'); return; }
    setAssigningWorksheet(true);
    try {
      await axios.post(`/notes/${internalNotesModal.id}/assign`, {
        target_type: wsAssignmentData.target_type,
        target_ids: targetIds,
        title: wsAssignmentData.title.trim(),
        description: wsAssignmentData.description || undefined,
        due_date: wsAssignmentData.due_date || undefined,
        hint_enabled: wsAssignmentData.hint_enabled,
        book_title: assigningChapterContext.bookTitle || undefined,
        chapter: assigningChapterContext.chapter || undefined,
        topic: assigningChapterContext.topic || undefined,
      });
      markChapterAssigned(selectedSubject, assigningChapterContext.bookTitle, assigningChapterContext.chapter, assigningChapterContext.topic, 'notes');
      alert('Notes assigned successfully!');
      setInternalNotesModal(null);
    } catch (error) {
      console.error('Error assigning note:', error);
      alert(error?.response?.data?.detail || 'Failed to assign notes. Please try again.');
    } finally {
      setAssigningWorksheet(false);
    }
  };

  const handleAssignVideo = async () => {
    if (!internalVideoModal?.video_id) return;
    const targetIds = wsAssignmentData.target_type === 'batch' ? wsSelectedBatches : wsSelectedStudents;
    if (!targetIds.length) { alert('Please select at least one recipient before assigning.'); return; }
    if (!wsAssignmentData.title.trim()) { alert('Please enter a task title.'); return; }
    setAssigningWorksheet(true);
    try {
      await axios.post(`/videos/${internalVideoModal.video_id}/assign`, {
        target_type: wsAssignmentData.target_type,
        target_ids: targetIds,
        title: wsAssignmentData.title.trim(),
        description: wsAssignmentData.description || undefined,
        book_title: assigningChapterContext.bookTitle || undefined,
        chapter: assigningChapterContext.chapter || undefined,
        topic: assigningChapterContext.topic || undefined,
      });
      markChapterAssigned(selectedSubject, assigningChapterContext.bookTitle, assigningChapterContext.chapter, assigningChapterContext.topic, 'video');
      alert('Video assigned successfully!');
      setInternalVideoModal(null);
    } catch (error) {
      console.error('Error assigning video:', error);
      alert(error?.response?.data?.detail || 'Failed to assign video. Please try again.');
    } finally {
      setAssigningWorksheet(false);
    }
  };

  // Both dialogs read from the same GET /tasks/{id}/comparison endpoint the
  // coaching task cards use: the response carries `.evaluation` (Full Analysis)
  // and `.comparison_data` (Compare), so one fetch drives either dialog.
  const fetchComparison = async (taskId) => {
    const response = await axios.get(`/tasks/${taskId}/comparison`);
    return response.data;
  };

  const handleViewFullAnalysis = async (taskId) => {
    if (!taskId || analysisLoading) return;
    setAnalysisLoading(true);
    try {
      const data = await fetchComparison(taskId);
      if (!data?.evaluation) {
        alert('No evaluation found for this task.');
        return;
      }
      setFullAnalysisData(data.evaluation);
      setShowFullAnalysis(true);
    } catch (error) {
      console.error('Error loading task analysis:', error);
      alert(error?.response?.data?.detail || 'Could not load the analysis for this task.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const handleCompareAnswers = async (taskId) => {
    if (!taskId || analysisLoading) return;
    setAnalysisLoading(true);
    try {
      const data = await fetchComparison(taskId);
      setCompareData(data);
      setShowCompare(true);
    } catch (error) {
      console.error('Error loading answer comparison:', error);
      alert(error?.response?.data?.detail || 'Could not load the comparison for this task.');
    } finally {
      setAnalysisLoading(false);
    }
  };

  const getChapterCardKey = (bookTitle, chapter) => `${bookTitle}::${chapter}`;

  const toggleChapterExpansion = (bookTitle, chapter) => {
    const chapterKey = getChapterCardKey(bookTitle, chapter);
    setExpandedChapters((prev) => ({ ...prev, [chapterKey]: !prev[chapterKey] }));
  };

  const renderMaterialLinks = ({ subject, bookTitle, chapter, topic = '' }) => (
    <div className="flex flex-wrap gap-1.5">
      {materialButtonConfig.map((button) => {
        // When assignment is allowed (parent/teacher), Sheet/Notes/Video move
        // into the consolidated "Assign" dropdown below instead of showing
        // here too — read-only contexts (e.g. child) keep seeing them here
        // since they have no Assign button at all.
        if (allowAssignment && ['worksheet', 'video', 'html'].includes(button.type)) return null;
        if (!allowAssignment && button.type === 'worksheet') return null;
        const availability = getMaterialAvailability({
          subject,
          bookTitle,
          chapter,
          topic,
        });
        const typeCount = availability?.by_type?.[button.type] || 0;
        if (!typeCount) return null;
        return (
          <button
            key={`${bookTitle}-${chapter}-${topic || 'chapter'}-${button.type}`}
            type="button"
            onClick={() =>
              handleOpenMaterial({
                subject,
                bookTitle,
                chapter,
                topic,
                materialType: button.type
              })
            }
            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] sm:text-xs font-semibold text-gray-800 ${button.className}`}
            title={`Open ${button.label} material`}
          >
            <span>{button.icon}</span>
            <span>{button.label}</span>
            {/*<span className="rounded bg-white/25 px-1">{typeCount}</span>*/}
          </button>
        );
      })}
    </div>
  );

  // Shared small dropdown used by both the "Create" and "Assign" buttons at
  // chapter and topic level — a trigger button that toggles a list of
  // options, tracked via the single `openMenu` state so only one dropdown
  // is open at a time.
  const renderDropdownButton = ({ rowKey, kind, triggerLabel, triggerIcon, triggerClassName, items, emptyLabel = null }) => {
    if (!items || items.length === 0) {
      if (!emptyLabel) return null;
    }
    const isOpen = openMenu?.key === rowKey && openMenu?.kind === kind;
    return (
      <div className="relative inline-block">
        <button
          type="button"
          onClick={() => setOpenMenu(isOpen ? null : { key: rowKey, kind })}
          className={triggerClassName}
        >
          <span>{triggerIcon}</span>
          <span>{triggerLabel}</span>
          <span className="text-[9px]">{isOpen ? '▲' : '▼'}</span>
        </button>
        {isOpen && (
          <div className="absolute left-0 top-full z-20 mt-1 min-w-[150px] rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {items.length > 0 ? items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs sm:text-sm text-gray-800 hover:bg-gray-100"
              >
                <span>{item.icon}</span>
                <span>{item.label}</span>
              </button>
            )) : (
              <div className="px-3 py-1.5 text-xs text-gray-400">{emptyLabel}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleOpenWorksheetGenerator = ({ subject, bookTitle, chapter, topic = '', chapterTopics = [] }) => {
    if (typeof onOpenWorksheetGenerator !== 'function') {
      return;
    }

    onOpenWorksheetGenerator({
      subject,
      bookTitle,
      chapter,
      topic,
      chapterTopics,
    });
  };

  // The four "Create" options, each backed by its own optional callback prop
  // (only passed by dashboards that support that generator) — mirrors the
  // single onOpenWorksheetGenerator contract above so Dashboard.jsx/
  // TeacherDashboard.jsx wire them the same way.
  const createMenuConfig = [
    { kind: 'worksheet', label: 'Sheet', icon: '📝', handler: onOpenWorksheetGenerator },
    { kind: 'notes', label: 'Notes', icon: '📓', handler: onOpenNotesGenerator },
    { kind: 'video', label: 'Video', icon: '🎬', handler: onOpenVideoGenerator },
    { kind: 'lesson_kit', label: 'Lesson Kit', icon: '🧰', handler: onOpenLessonKit },
  ].filter((item) => typeof item.handler === 'function');

  const handleCreateMenuSelect = (handler, { subject, bookTitle, chapter, topic = '', chapterTopics = [] }) => {
    if (typeof handler !== 'function') return;
    handler({ subject, bookTitle, chapter, topic, chapterTopics });
    setOpenMenu(null);
  };

  const _activeMaterialSubject = (
    internalWorksheetModal?.worksheet?.subject ||
    internalNotesModal?.subject ||
    internalVideoModal?.subject ||
    ''
  ).toLowerCase();

  const wsFiltBatches = (() => {
    const batches = wsAssignmentTargets.batches || [];
    if (!_activeMaterialSubject) return batches;
    return batches.filter(b => (b.subject || '').toLowerCase() === _activeMaterialSubject);
  })();

  const wsFiltStudents = (() => {
    const students = wsAssignmentTargets.students || [];
    if (!wsSelectedBatches.length) return students;
    return students.filter(s => {
      const batchIds = (s.batch_ids || (s.batch_id ? [s.batch_id] : [])).map(String);
      return wsSelectedBatches.some(bId => batchIds.includes(String(bId)));
    });
  })();

  return (
    <div className={renderAsPage ? 'w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'}>
      <div className={`bg-gradient-to-br from-purple-50 via-white to-indigo-50 rounded-2xl w-full overflow-hidden flex flex-col border border-indigo-100 ${renderAsPage ? 'shadow-lg' : 'max-w-4xl max-h-[90vh] shadow-2xl'}`}>
        <div className="sticky top-0 z-10 p-6 border-b border-green-900 bg-gradient-to-r from-green-700 via-green-800 to-purple-600 text-white relative">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pr-10">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-white/20 text-white flex items-center justify-center text-2xl shadow-inner">📘</div>
              <div>
                <h2 className="text-xl font-semibold leading-tight">
                  Books for {getFirstName(child?.name) || child?.name}
                </h2>
                <p className="text-xs text-white/80">
                  Select books and view syllabus content.
                </p>
                <p className="text-xs text-white/80">
                  Grade {child?.grade} • {child?.board || 'Board not set'}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={() => onClose && onClose(null)}
            aria-label="Close"
            className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all absolute right-4 sm:right-6 top-4 sm:top-6"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-700">Loading syllabus...</p>
          </div>
        ) : (
          <div className="overflow-y-auto p-3 sm:p-8 space-y-3 sm:space-y-6">
            {(subjects.length > 0 || (currentUser?.role === 'parent' && allowBookManagement)) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 items-start">
                {/* Subject Selector */}
                {subjects.length > 0 && (
                  <div className="rounded-lg border border-gray-200 bg-white/90 p-2.5 sm:p-4 h-full">
                    <label className="block text-xs sm:text-sm font-medium text-blue-800 mb-1 sm:mb-2"><strong>Select Subject</strong></label>
                    <select
                      value={selectedSubject}
                      onChange={(e) => setSelectedSubject(e.target.value)}
                      className="w-full px-2 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      data-testid="syllabus-subject-select"
                    >
                      {subjects.map(subject => (
                        <option key={subject} value={subject}>{subject}</option>
                      ))}
                    </select>
                  </div>
                )}

                {currentUser?.role === 'parent' && allowBookManagement && (
                  <div className="rounded-lg sm:rounded-xl border border-blue-100 bg-blue-100 p-2.5 sm:p-4 text-xs sm:text-sm text-blue-900 h-full">
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 font-semibold">
                      <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 sm:px-3 sm:py-1 text-[10px] sm:text-xs uppercase tracking-wide text-blue-800 shadow-sm">
                        Curriculum
                      </span>
                      <span className="text-xs sm:text-sm">
                        {usesSchoolCurriculum
                          ? 'School curriculum available'
                          : 'School curriculum not available'}
                      </span>
                    </div>
                    <p className="mt-1 text-[11px] sm:hidden">
                      {usesSchoolCurriculum
                        ? (isSelectionLocked ? 'School-selected books are locked.' : 'School books are selected by default.')
                        : 'Board books are selected by default.'}
                    </p>
                    <p className="mt-2 text-xs hidden sm:block">
                      {usesSchoolCurriculum
                        ? (isSelectionLocked
                          ? 'Books are pre-selected from your school curriculum and locked by your school.'
                          : 'Books are pre-selected from your school curriculum by default. You can change the selection anytime.')
                        : 'Books default to Board curriculum. You can customize the selection from available books anytime.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Book Management Section */}
            {allowBookManagement && (
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={handleToggleBookManagement}
                  className="flex items-center justify-between w-full px-4 py-3 text-white bg-gradient-to-r from-green-600 via-green-700 to-purple-600 rounded-lg hover:from-green-800 hover:via-green-800 hover:to-purple-700 transition-all shadow-md"
                >
                  <span className="font-semibold flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Select Books
                  </span>
                  <svg
                    className={`w-5 h-5 transform transition-transform ${showBookManagement ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showBookManagement && (
                  <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h4 className="text-lg font-semibold text-purple-900 mb-3">📚 Select Preferred Books</h4>
                  <p className="text-sm text-gray-700 mb-4">
                    Select one or more books to filter worksheet generation, notes, and syllabus display. 
                    {selectedBooks.length === 0 && " If no books are selected, the system will use the Master syllabus."}
                  </p>
                  {isSelectionLocked && (
                    <div className="mb-4 rounded-lg border border-purple-200 bg-white px-3 py-2 text-xs text-purple-800">
                      Your school has onboarded its curriculum. Book selection is locked.
                    </div>
                  )}

                  {/* Removed board selector - now showing books from all boards */}

                  {loadingBooks ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                      <p className="text-gray-600 text-sm">Loading books...</p>
                    </div>
                  ) : availableBooks.length > 0 ? (
                    <div className="space-y-3">
                      {/* Selected books count */}
                      <div className="bg-white rounded-lg p-3 border border-purple-300">
                        <p className="text-sm font-medium text-gray-700">
                          Selected: <span className="text-purple-600 font-bold">
                            {availableBooks.filter((book) =>
                              normalizedSelectedBooks.has(normalizeBookTitle(book.book_title))
                            ).length}
                          </span> / {availableBooks.length} books for {selectedSubject}
                        </p>
                      </div>

                      {/* Book cards grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                        {availableBooks.map((book, index) => {
                          const isRequired = requiredBookSet.has(normalizeBookTitle(book.book_title));
                          const disabled = isSelectionLocked || isRequired;
                          return (
                          <div
                            key={index}
                            className={`border-2 rounded-lg p-3 transition-all ${
                              normalizedSelectedBooks.has(normalizeBookTitle(book.book_title))
                                ? 'border-purple-500 bg-purple-100'
                                : 'border-gray-300 bg-white hover:border-purple-300'
                            } ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={() => handleToggleBookSelection(book.book_title)}
                            title={isRequired ? 'Required by your school curriculum' : undefined}
                          >
                            <div className="flex items-start space-x-3">
                              {/* Checkbox */}
                              <div className="flex-shrink-0 mt-1">
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                  normalizedSelectedBooks.has(normalizeBookTitle(book.book_title))
                                    ? 'bg-purple-500 border-purple-500'
                                    : 'border-gray-400'
                                }`}>
                                  {normalizedSelectedBooks.has(normalizeBookTitle(book.book_title)) && (
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>

                              {/* Book thumbnail */}
                              {book.thumbnail_url ? (
                                <div className="flex-shrink-0">
                                  <img
                                    src={book.thumbnail_url}
                                    alt={book.book_title}
                                    className="w-16 h-20 object-cover rounded shadow-sm"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="flex-shrink-0">
                                  <div className="w-16 h-20 flex items-center justify-center rounded border border-dashed border-gray-300 bg-white text-2xl" aria-label="No thumbnail available">
                                    <span className="text-6xl leading-none">📘</span>
                                  </div>
                                </div>
                              )}

                              {/* Book details */}
                              <div className="flex-1 min-w-0">
                                <h5 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                                  {book.book_title}
                                </h5>
                                <p className="text-xs text-gray-600 mb-1">{book.publisher}</p>
                                <div className="flex items-center text-xs text-gray-500 space-x-2">
                                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                    {book.subject}
                                  </span>
                                  {book.source_board && (
                                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                      {book.source_board}
                                    </span>
                                  )}
                                  <span>
                                    {book.chapter_count} chapters
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>

                      {/* Save button */}
                      <div className="flex justify-end space-x-3 pt-3">
                        <button
                          onClick={() => setShowBookManagement(false)}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveBookSelection}
                          disabled={isSelectionLocked}
                          className={`px-4 py-2 rounded-lg shadow-md ${
                            isSelectionLocked
                              ? 'bg-purple-300 text-white/80 cursor-not-allowed'
                              : 'bg-purple-600 text-white hover:bg-purple-700'
                          }`}
                        >
                          Save Selection
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-4xl mb-2">📚</div>
                      <p className="text-gray-600 text-sm">
                        No books available for this subject, grade, and board.
                      </p>
                    </div>
                  )}
                  </div>
                )}
              </div>
            )}

            {/* Syllabus Content */}
            {getSubjectSyllabus() ? (
              <div className="">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{selectedSubject} Syllabus</h3>
                
                {/* Check if syllabus has books array (new structure) */}
                {getSubjectSyllabus().books ? (
                  <div className="space-y-4">
                    {/* Book selector — one pill per selected book. Only the
                        active book's chapters render below, so the panel no
                        longer grows into a very long scroll. The row scrolls
                        horizontally on mobile and wraps on wider screens. */}
                    {subjectBooks.length > 1 && (
                      <div
                        role="tablist"
                        aria-label="Select a book to view its syllabus"
                        className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 sm:flex-wrap sm:overflow-visible"
                      >
                        {subjectBooks.map((book, idx) => {
                          const chapterCount = Object.keys(book.chapters || {}).length;
                          const isActive = idx === activeBookIdx;
                          return (
                            <button
                              key={idx}
                              type="button"
                              role="tab"
                              aria-selected={isActive}
                              onClick={() => setActiveBookIndex(idx)}
                              title={book.title}
                              className={`flex-shrink-0 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors ${
                                isActive
                                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <span className="max-w-[9rem] sm:max-w-[14rem] truncate">{book.title}</span>
                              <span
                                className={`flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                                  isActive ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'
                                }`}
                                title={`${chapterCount} chapter${chapterCount === 1 ? '' : 's'}`}
                              >
                                {chapterCount}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {getSubjectSyllabus().books.map((book, bookIndex) => bookIndex === activeBookIdx && (
                      <div key={bookIndex} className="border border-gray-300 rounded-lg overflow-hidden">
                        {/* Book Header */}
                        <div className="bg-blue-100 p-4">
                          <h4 className="text-lg font-semibold text-blue-900">{book.title}</h4>
                          <p className="text-sm text-blue-700">
                            {book.publisher} | {book.authors && Array.isArray(book.authors) ? book.authors.join(', ') : 'Unknown Author'}
                          </p>
                          {book.isbn_13 && (
                            <p className="text-xs text-blue-600 mt-1">ISBN: {book.isbn_13}</p>
                          )}
                        </div>
                        
                        {/* Chapters */}
                        <div className="p-4 space-y-3 mx-auto">
                          {Object.entries(book.chapters).map(([chapter, topics]) => {
                            const chapterRowKey = getChapterCardKey(book.title, chapter);
                            const chapterAssignmentInfo = getAssignmentStatus({ subject: selectedSubject, bookTitle: book.title, chapter });
                            return (
                            <div
                              key={chapter}
                              className={`rounded-lg p-2 shadow-sm border ${getAssignmentCardClass(chapterAssignmentInfo)}`}
                            >
                              <div className="mb-1 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                  <button
                                    type="button"
                                    onClick={() => toggleChapterExpansion(book.title, chapter)}
                                    className="flex min-w-0 items-center gap-2 text-left"
                                  >
                                    <span className="text-xs text-gray-600">
                                      {expandedChapters[chapterRowKey] ? '▼' : '▶'}
                                    </span>
                                    <h5 className="text-sm sm:text-base font-semibold text-gray-900">
                                      {chapter}
                                    </h5>
                                  </button>

                                  {renderAssignmentBadge(chapterAssignmentInfo)}
                                  {renderScoreAndAnalysis(chapterAssignmentInfo)}

                                  {createMenuConfig.length > 0 && renderDropdownButton({
                                    rowKey: chapterRowKey,
                                    kind: 'create',
                                    triggerLabel: 'Create',
                                    triggerIcon: '✨',
                                    triggerClassName: 'inline-flex max-w-full min-w-0 items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-[10px] sm:text-xs font-semibold hover:bg-blue-200',
                                    items: createMenuConfig.map((cfg) => ({
                                      key: cfg.kind,
                                      icon: cfg.icon,
                                      label: cfg.label,
                                      onClick: () => handleCreateMenuSelect(cfg.handler, {
                                        subject: selectedSubject,
                                        bookTitle: book.title,
                                        chapter,
                                        chapterTopics: Array.isArray(topics) ? topics : [topics],
                                      }),
                                    })),
                                  })}

                                  {allowAssignment && renderDropdownButton({
                                    rowKey: chapterRowKey,
                                    kind: 'assign',
                                    triggerLabel: 'Assign',
                                    triggerIcon: '🎯',
                                    triggerClassName: 'inline-flex max-w-full min-w-0 items-center gap-1 rounded-md bg-emerald-100 px-2 py-1 text-[10px] sm:text-xs font-semibold hover:bg-emerald-200',
                                    items: getAssignableMaterials({ subject: selectedSubject, bookTitle: book.title, chapter }).map((cfg) => ({
                                      key: cfg.type,
                                      icon: cfg.icon,
                                      label: `${cfg.label} (${cfg.count})`,
                                      onClick: () => {
                                        setOpenMenu(null);
                                        handleOpenMaterial({ subject: selectedSubject, bookTitle: book.title, chapter, materialType: cfg.type });
                                      },
                                    })),
                                    emptyLabel: 'No mapped content yet',
                                  })}
                                </div>

                                <div className="px-2 py-1 text-xs text-gray-500 whitespace-nowrap">
                                  {Array.isArray(topics) ? topics.length : 1} topics
                                </div>
                              </div>
                              <div className="mb-3">
                                {renderMaterialLinks({
                                  subject: selectedSubject,
                                  bookTitle: book.title,
                                  chapter
                                })}
                              </div>

                              {expandedChapters[chapterRowKey] && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {(Array.isArray(topics) ? topics : [topics]).map((topic, topicIndex) => {
                                    const topicRowKey = `${chapterRowKey}::${topic}`;
                                    const topicAssignmentInfo = getAssignmentStatus({ subject: selectedSubject, bookTitle: book.title, chapter, topic });
                                    return (
                                    <div
                                      key={topicIndex}
                                      className={`rounded-md border p-2 ${
                                        topicAssignmentInfo.status === 'completed'
                                          ? 'bg-green-50 border-green-300'
                                          : topicAssignmentInfo.assigned
                                            ? 'bg-amber-50 border-amber-300'
                                            : 'bg-blue-50 border-blue-100'
                                      }`}
                                    >
                                      <div className="mb-2 flex items-start justify-between gap-2">
                                        <div className="text-xs sm:text-sm text-gray-800 font-medium flex-1 flex items-center gap-1 flex-wrap">
                                          <span>{topic}</span>
                                          {topicAssignmentInfo.status === 'completed' ? (
                                            <span
                                              className="inline-flex items-center gap-1 rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-semibold text-green-700"
                                              title={`Completed: ${topicAssignmentInfo.types.join(', ')}`}
                                            >
                                              ✅
                                            </span>
                                          ) : topicAssignmentInfo.assigned ? (
                                            <span
                                              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700"
                                              title={`Assigned: ${topicAssignmentInfo.types.join(', ')}`}
                                            >
                                              🕐
                                            </span>
                                          ) : null}
                                          {renderScoreAndAnalysis(topicAssignmentInfo)}
                                        </div>

                                        <div className="flex items-center gap-1 flex-wrap justify-end">
                                          {createMenuConfig.length > 0 && renderDropdownButton({
                                            rowKey: topicRowKey,
                                            kind: 'create',
                                            triggerLabel: 'Create',
                                            triggerIcon: '✨',
                                            triggerClassName: 'inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-[10px] sm:text-xs font-semibold hover:bg-purple-200 whitespace-nowrap',
                                            items: createMenuConfig.map((cfg) => ({
                                              key: cfg.kind,
                                              icon: cfg.icon,
                                              label: cfg.label,
                                              onClick: () => handleCreateMenuSelect(cfg.handler, {
                                                subject: selectedSubject,
                                                bookTitle: book.title,
                                                chapter,
                                                topic,
                                              }),
                                            })),
                                          })}

                                          {allowAssignment && renderDropdownButton({
                                            rowKey: topicRowKey,
                                            kind: 'assign',
                                            triggerLabel: 'Assign',
                                            triggerIcon: '🎯',
                                            triggerClassName: 'inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-1 text-[10px] sm:text-xs font-semibold hover:bg-emerald-200 whitespace-nowrap',
                                            items: getAssignableMaterials({ subject: selectedSubject, bookTitle: book.title, chapter, topic }).map((cfg) => ({
                                              key: cfg.type,
                                              icon: cfg.icon,
                                              label: `${cfg.label} (${cfg.count})`,
                                              onClick: () => {
                                                setOpenMenu(null);
                                                handleOpenMaterial({ subject: selectedSubject, bookTitle: book.title, chapter, topic, materialType: cfg.type });
                                              },
                                            })),
                                            emptyLabel: 'No mapped content yet',
                                          })}
                                        </div>
                                      </div>
                                      {renderMaterialLinks({
                                        subject: selectedSubject,
                                        bookTitle: book.title,
                                        chapter,
                                        topic,
                                      })}
                                    </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  /* Old structure fallback (if any old data exists) */
                  <div className="space-y-4">
                    {Object.entries(getSubjectSyllabus()).map(([unit, topics]) => (
                      <div key={unit} className="bg-white rounded-lg p-4 shadow-sm">
                        <h4 className="text-xs font-medium text-blue-600 mb-2">{unit}</h4>
                        <ul className="space-y-1">
                          {Array.isArray(topics) ? topics.map((topic, index) => (
                            <li key={index} className="text-gray-700 flex items-start">
                              <span className="text-blue-500 mr-2">•</span>
                              {topic}
                            </li>
                          )) : (
                            <li className="text-gray-700 flex items-start">
                              <span className="text-blue-500 mr-2">•</span>
                              {String(topics)}
                            </li>
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">📚</div>
                <p className="text-gray-600">
                  {selectedSubject ? 
                    `Syllabus for ${selectedSubject} is not available yet.` : 
                    'No syllabus data available.'
                  }
                </p>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2 mt-4 px-2 py-2">
          <button
            onClick={onClose}
            className="py-2 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="close-syllabus-btn"
          >
            Close
          </button>
        </div>
      </div>

      {/* Internal Video Modal */}
      {internalVideoModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
          <div className={`bg-white rounded-2xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${allowAssignment ? 'max-w-5xl' : 'max-w-3xl'}`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-orange-500 p-6 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs uppercase text-red-100 tracking-wide">{allowAssignment ? 'Video Assignment' : 'Video'}</p>
                  <h3 className="text-xl font-semibold text-white">{internalVideoModal.title || 'Video'}</h3>
                  <p className="text-sm text-red-100 mt-1">{allowAssignment ? 'Watch and assign this video to your students.' : 'Watch this video.'}</p>
                </div>
                <button onClick={() => setInternalVideoModal(null)} aria-label="Close" className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"><svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
            </div>

            {/* Body: two-column */}
            <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
              {/* Video player */}
              <div className={`${allowAssignment ? 'w-full md:w-1/2 border-b md:border-b-0 md:border-r' : 'w-full'} border-gray-200 bg-black flex flex-col min-h-0`}>
                <div className="flex-1 overflow-auto">
                  <VideoPlayer video={internalVideoModal} onClose={() => setInternalVideoModal(null)} inline />
                </div>
              </div>

              {/* Right: assignment form (hidden for children) */}
              {allowAssignment && <div className="w-full md:w-1/2 overflow-y-auto p-6 space-y-4 bg-gray-50">
                {/* Task Title */}
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Task Title *</label>
                  <input
                    type="text"
                    value={wsAssignmentData.title}
                    onChange={(e) => setWsAssignmentData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Science - Motion Video"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 bg-white"
                  />
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Description (Optional)</label>
                  <textarea
                    value={wsAssignmentData.description}
                    onChange={(e) => setWsAssignmentData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Additional instructions..."
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 bg-white"
                  />
                </div>

                {/* Target Selection */}
                {wsLoadingTargets ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                    Loading recipients...
                  </div>
                ) : currentUser?.role === 'parent' ? (
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-gray-800">Select Children</label>
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                      {(wsAssignmentTargets.children || []).map(c => (
                        <label key={c.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                          <input type="checkbox" checked={wsSelectedStudents.includes(c.id)}
                            onChange={(e) => setWsSelectedStudents(prev => e.target.checked ? (prev.includes(c.id) ? prev : [...prev, c.id]) : prev.filter(id => id !== c.id))}
                            className="rounded border-gray-300" />
                          <span className="text-sm text-gray-800">{c.name} (Grade {c.grade})</span>
                        </label>
                      ))}
                    </div>
                    {wsSelectedStudents.length > 0 && <p className="text-xs text-green-600 mt-2">{wsSelectedStudents.length} child(ren) selected</p>}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-800 mb-3">Assign To</label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer ${wsAssignmentData.target_type === 'child' ? 'border-red-200 bg-red-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                          <input type="radio" name="vid_target_type" value="child" checked={wsAssignmentData.target_type === 'child'} onChange={() => setWsAssignmentData(prev => ({ ...prev, target_type: 'child' }))} className="text-red-600" />
                          <span className="text-sm text-gray-800">Individual Students</span>
                        </label>
                        <label className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer ${wsAssignmentData.target_type === 'batch' ? 'border-red-200 bg-red-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                          <input type="radio" name="vid_target_type" value="batch" checked={wsAssignmentData.target_type === 'batch'} onChange={() => setWsAssignmentData(prev => ({ ...prev, target_type: 'batch' }))} className="text-red-600" />
                          <span className="text-sm text-gray-800">Batches</span>
                        </label>
                      </div>
                    </div>
                    {wsAssignmentData.target_type === 'child' && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Select Students</label>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                          {wsFiltStudents.map(s => (
                            <label key={s.id} className="flex items-center mb-1 px-2 py-1 rounded hover:bg-white">
                              <input type="checkbox" checked={wsSelectedStudents.includes(s.id)}
                                onChange={(e) => setWsSelectedStudents(prev => e.target.checked ? (prev.includes(s.id) ? prev : [...prev, s.id]) : prev.filter(id => id !== s.id))}
                                className="mr-2 rounded border-gray-300" />
                              <span className="text-sm text-gray-800">{s.name}</span>
                            </label>
                          ))}
                          {wsFiltStudents.length === 0 && <p className="text-sm text-gray-500 px-2 py-1">No students available.</p>}
                        </div>
                      </div>
                    )}
                    {wsAssignmentData.target_type === 'batch' && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Select Batches</label>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                          {wsFiltBatches.length === 0 && <p className="text-sm text-gray-500 px-2 py-1">No batches available for this subject.</p>}
                          {wsFiltBatches.map(b => (
                            <label key={b.id} className="flex items-center mb-1 px-2 py-1 rounded hover:bg-white">
                              <input type="checkbox" checked={wsSelectedBatches.includes(b.id)}
                                onChange={(e) => setWsSelectedBatches(prev => e.target.checked ? (prev.includes(b.id) ? prev : [...prev, b.id]) : prev.filter(id => id !== b.id))}
                                className="mr-2 rounded border-gray-300" />
                              <span className="text-sm text-gray-800">{currentUser?.role === 'school_teacher' ? `Class ${b.name}` : b.name} ({b.student_count} students)</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-white flex-shrink-0">
              {allowAssignment ? (
                <>
                  <div className="text-sm text-gray-500">Assign this video to students from the syllabus.</div>
                  <div className="flex space-x-3">
                    <button onClick={() => setInternalVideoModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button onClick={handleAssignVideo} disabled={assigningWorksheet} className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 font-medium shadow">
                      {assigningWorksheet ? 'Assigning...' : 'Assign Video'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex justify-end w-full">
                  <button onClick={() => setInternalVideoModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Close</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Internal Notes Modal */}
      {internalNotesModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
          <div className={`bg-white rounded-2xl w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${allowAssignment ? 'max-w-5xl' : 'max-w-3xl'}`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs uppercase text-amber-100 tracking-wide">{allowAssignment ? 'Notes Assignment' : 'Notes'}</p>
                  <h3 className="text-xl font-semibold text-white">📄 {internalNotesModal.title || 'Notes'}</h3>
                  <p className="text-sm text-amber-100 mt-1">{allowAssignment ? 'Read and assign these notes to your students.' : 'Read these notes.'}</p>
                </div>
                <button onClick={() => setInternalNotesModal(null)} aria-label="Close" className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"><svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
              </div>
            </div>

            {/* Body: two-column */}
            <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
              {/* Notes content */}
              <div className={`${allowAssignment ? 'w-full md:w-1/2 border-b md:border-b-0 md:border-r' : 'w-full'} border-gray-200 bg-gray-50 flex flex-col min-h-0`}>
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-700">📄 Notes Content</span>
                </div>
                <div className="flex-1 overflow-auto p-1">
                  <iframe
                    srcDoc={internalNotesModal.html_content}
                    title="Notes"
                    className="w-full border-0"
                    style={{ minHeight: '400px', height: '100%' }}
                    sandbox="allow-same-origin"
                  />
                </div>
              </div>

              {/* Right: assignment form (hidden for children) */}
              {allowAssignment && <div className="w-full md:w-1/2 overflow-y-auto p-6 space-y-4 bg-gray-50">
                {/* Task Title */}
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Task Title *</label>
                  <input
                    type="text"
                    value={wsAssignmentData.title}
                    onChange={(e) => setWsAssignmentData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Science - Chapter Notes"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-amber-500 bg-white"
                  />
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Description (Optional)</label>
                  <textarea
                    value={wsAssignmentData.description}
                    onChange={(e) => setWsAssignmentData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Additional instructions..."
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-amber-500 bg-white"
                  />
                </div>

                {/* Due Date + Hint */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-800">Due Date (Optional)</label>
                    <input
                      type="datetime-local"
                      value={wsAssignmentData.due_date}
                      onChange={(e) => setWsAssignmentData(prev => ({ ...prev, due_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-amber-500 bg-white"
                    />
                  </div>
                  <div className="space-y-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <label className="flex cursor-pointer items-start space-x-2">
                      <input type="checkbox" checked={wsAssignmentData.hint_enabled}
                        onChange={(e) => setWsAssignmentData(prev => ({ ...prev, hint_enabled: e.target.checked }))}
                        className="mt-1 w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500" />
                      <span className="text-sm text-blue-900">
                        <span className="font-semibold block">Enable Hints</span>
                        <span className="text-xs">Let students request hints during the task.</span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Target Selection */}
                {wsLoadingTargets ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                    Loading recipients...
                  </div>
                ) : currentUser?.role === 'parent' ? (
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-gray-800">Select Children</label>
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                      {(wsAssignmentTargets.children || []).map(c => (
                        <label key={c.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                          <input type="checkbox" checked={wsSelectedStudents.includes(c.id)}
                            onChange={(e) => setWsSelectedStudents(prev => e.target.checked ? (prev.includes(c.id) ? prev : [...prev, c.id]) : prev.filter(id => id !== c.id))}
                            className="rounded border-gray-300" />
                          <span className="text-sm text-gray-800">{c.name} (Grade {c.grade})</span>
                        </label>
                      ))}
                    </div>
                    {wsSelectedStudents.length > 0 && <p className="text-xs text-green-600 mt-2">{wsSelectedStudents.length} child(ren) selected</p>}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-800 mb-3">Assign To</label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer ${wsAssignmentData.target_type === 'child' ? 'border-amber-200 bg-amber-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                          <input type="radio" name="note_target_type" value="child" checked={wsAssignmentData.target_type === 'child'} onChange={() => setWsAssignmentData(prev => ({ ...prev, target_type: 'child' }))} className="text-amber-600" />
                          <span className="text-sm text-gray-800">Individual Students</span>
                        </label>
                        <label className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer ${wsAssignmentData.target_type === 'batch' ? 'border-amber-200 bg-amber-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                          <input type="radio" name="note_target_type" value="batch" checked={wsAssignmentData.target_type === 'batch'} onChange={() => setWsAssignmentData(prev => ({ ...prev, target_type: 'batch' }))} className="text-amber-600" />
                          <span className="text-sm text-gray-800">Batches</span>
                        </label>
                      </div>
                    </div>
                    {wsAssignmentData.target_type === 'child' && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Select Students</label>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                          {wsFiltStudents.map(s => (
                            <label key={s.id} className="flex items-center mb-1 px-2 py-1 rounded hover:bg-white">
                              <input type="checkbox" checked={wsSelectedStudents.includes(s.id)}
                                onChange={(e) => setWsSelectedStudents(prev => e.target.checked ? (prev.includes(s.id) ? prev : [...prev, s.id]) : prev.filter(id => id !== s.id))}
                                className="mr-2 rounded border-gray-300" />
                              <span className="text-sm text-gray-800">{s.name}</span>
                            </label>
                          ))}
                          {wsFiltStudents.length === 0 && <p className="text-sm text-gray-500 px-2 py-1">No students available.</p>}
                        </div>
                      </div>
                    )}
                    {wsAssignmentData.target_type === 'batch' && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Select Batches</label>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                          {wsFiltBatches.length === 0 && <p className="text-sm text-gray-500 px-2 py-1">No batches available for this subject.</p>}
                          {wsFiltBatches.map(b => (
                            <label key={b.id} className="flex items-center mb-1 px-2 py-1 rounded hover:bg-white">
                              <input type="checkbox" checked={wsSelectedBatches.includes(b.id)}
                                onChange={(e) => setWsSelectedBatches(prev => e.target.checked ? (prev.includes(b.id) ? prev : [...prev, b.id]) : prev.filter(id => id !== b.id))}
                                className="mr-2 rounded border-gray-300" />
                              <span className="text-sm text-gray-800">{currentUser?.role === 'school_teacher' ? `Class ${b.name}` : b.name} ({b.student_count} students)</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>}
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-white flex-shrink-0">
              {allowAssignment ? (
                <>
                  <div className="text-sm text-gray-500">Assign these notes to students from the syllabus.</div>
                  <div className="flex space-x-3">
                    <button onClick={() => setInternalNotesModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                    <button onClick={handleAssignNote} disabled={assigningWorksheet} className="px-5 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-60 font-medium shadow">
                      {assigningWorksheet ? 'Assigning...' : 'Assign Notes'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex justify-end w-full">
                  <button onClick={() => setInternalNotesModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Close</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Internal Worksheet Assign Modal */}
      {internalWorksheetModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-xs uppercase text-purple-100 tracking-wide">Worksheet Assignment</p>
                  <h3 className="text-xl font-semibold text-white">Assign Task</h3>
                  <p className="text-sm text-purple-100 mt-1">Share this worksheet with your students.</p>
                </div>
                <button
                  onClick={() => setInternalWorksheetModal(null)}
                  aria-label="Close"
                  className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* Body: two-column */}
            <div className="flex-1 overflow-hidden flex flex-col md:flex-row min-h-0">
              {/* Left: worksheet preview */}
              <div className="w-full md:w-1/2 border-b md:border-b-0 md:border-r border-gray-200 bg-gray-50 flex flex-col min-h-0">
                <div className="px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
                  <span className="text-sm font-semibold text-gray-700">📄 Preview</span>
                </div>
                <div className="flex-1 overflow-auto p-3">
                  {internalWorksheetModal.worksheet ? (
                    <EnhancedWorksheetDisplay
                      worksheet={internalWorksheetModal.worksheet}
                      showAnswerKey={false}
                      mode="online"
                      canEditDiagram={false}
                      canEditQuestions={false}
                      enableOneQuestionToggle={true}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                      Preview not available
                    </div>
                  )}
                </div>
              </div>

              {/* Right: assignment form */}
              <div className="w-full md:w-1/2 overflow-y-auto p-6 space-y-4 bg-gray-50">
                {/* Worksheet metadata */}
                <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                  <p className="font-medium text-gray-800 mb-1">
                    {internalWorksheetModal.worksheet.saved_title || (internalWorksheetModal.worksheet.topics || []).join(', ') || 'Worksheet'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {[
                      internalWorksheetModal.worksheet.subject,
                      internalWorksheetModal.worksheet.grade && `Grade ${internalWorksheetModal.worksheet.grade}`,
                      internalWorksheetModal.worksheet.num_questions && `${internalWorksheetModal.worksheet.num_questions} questions`,
                      internalWorksheetModal.worksheet.difficulty
                    ].filter(Boolean).join(' • ')}
                  </p>
                </div>

                {/* Task Title */}
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Task Title *</label>
                  <input
                    type="text"
                    value={wsAssignmentData.title}
                    onChange={(e) => setWsAssignmentData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Mathematics - Short Test"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 bg-white"
                  />
                </div>

                {/* Description */}
                <div className="space-y-1">
                  <label className="block text-sm font-semibold text-gray-800">Description (Optional)</label>
                  <textarea
                    value={wsAssignmentData.description}
                    onChange={(e) => setWsAssignmentData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Additional instructions or notes..."
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 bg-white"
                  />
                </div>

                {/* Due Date + Hint Enabled */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="block text-sm font-semibold text-gray-800">Due Date (Optional)</label>
                    <input
                      type="datetime-local"
                      value={wsAssignmentData.due_date}
                      onChange={(e) => setWsAssignmentData(prev => ({ ...prev, due_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg shadow-sm focus:ring-2 focus:ring-purple-500 bg-white"
                    />
                  </div>
                  <div className="space-y-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <label className="flex cursor-pointer items-start space-x-2">
                      <input
                        type="checkbox"
                        checked={wsAssignmentData.hint_enabled}
                        onChange={(e) => setWsAssignmentData(prev => ({ ...prev, hint_enabled: e.target.checked }))}
                        className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-blue-900">
                        <span className="font-semibold block">Enable Hints</span>
                        <span className="text-xs">Let students request hints during the task.</span>
                      </span>
                    </label>
                  </div>
                </div>

                {/* Target Selection */}
                {wsLoadingTargets ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    Loading recipients...
                  </div>
                ) : currentUser?.role === 'parent' ? (
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-semibold text-gray-800">Select Children</label>
                      <span className="text-xs text-gray-500">Choose who receives this task</span>
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                      {(wsAssignmentTargets.children || []).map(c => (
                        <label key={c.id} className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-white">
                          <input
                            type="checkbox"
                            checked={wsSelectedStudents.includes(c.id)}
                            onChange={(e) => setWsSelectedStudents(prev =>
                              e.target.checked
                                ? (prev.includes(c.id) ? prev : [...prev, c.id])
                                : prev.filter(id => id !== c.id)
                            )}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-800">{c.name} (Grade {c.grade})</span>
                        </label>
                      ))}
                    </div>
                    {wsSelectedStudents.length > 0 && (
                      <p className="text-xs text-green-600 mt-2">{wsSelectedStudents.length} child(ren) selected</p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                      <label className="block text-sm font-semibold text-gray-800 mb-3">Assign To</label>
                      <div className="grid grid-cols-2 gap-3">
                        <label className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer ${wsAssignmentData.target_type === 'child' ? 'border-purple-200 bg-purple-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                          <input
                            type="radio"
                            name="ws_target_type"
                            value="child"
                            checked={wsAssignmentData.target_type === 'child'}
                            onChange={() => setWsAssignmentData(prev => ({ ...prev, target_type: 'child' }))}
                            className="text-purple-600"
                          />
                          <span className="text-sm text-gray-800">Individual Students</span>
                        </label>
                        <label className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer ${wsAssignmentData.target_type === 'batch' ? 'border-purple-200 bg-purple-50 shadow-inner' : 'border-gray-200 bg-gray-50'}`}>
                          <input
                            type="radio"
                            name="ws_target_type"
                            value="batch"
                            checked={wsAssignmentData.target_type === 'batch'}
                            onChange={() => setWsAssignmentData(prev => ({ ...prev, target_type: 'batch' }))}
                            className="text-purple-600"
                          />
                          <span className="text-sm text-gray-800">Batches</span>
                        </label>
                      </div>
                    </div>

                    {wsAssignmentData.target_type === 'child' && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-800">Select Students</label>
                          <span className="text-xs text-gray-500">Multi-select allowed</span>
                        </div>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                          {wsFiltStudents.map(s => (
                            <label key={s.id} className="flex items-center mb-1 px-2 py-1 rounded hover:bg-white">
                              <input
                                type="checkbox"
                                checked={wsSelectedStudents.includes(s.id)}
                                onChange={(e) => setWsSelectedStudents(prev =>
                                  e.target.checked
                                    ? (prev.includes(s.id) ? prev : [...prev, s.id])
                                    : prev.filter(id => id !== s.id)
                                )}
                                className="mr-2 rounded border-gray-300"
                              />
                              <span className="text-sm text-gray-800">{s.name}</span>
                            </label>
                          ))}
                          {wsFiltStudents.length === 0 && (
                            <p className="text-sm text-gray-500 px-2 py-1">No students available for this subject.</p>
                          )}
                        </div>
                      </div>
                    )}

                    {wsAssignmentData.target_type === 'batch' && (
                      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-800">Select Batches</label>
                          <span className="text-xs text-gray-500">Perfect for whole-class tasks</span>
                        </div>
                        <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                          {wsFiltBatches.length === 0 && (
                            <p className="text-sm text-gray-500 px-2 py-1">No batches available for this subject.</p>
                          )}
                          {wsFiltBatches.map(b => (
                            <label key={b.id} className="flex items-center mb-1 px-2 py-1 rounded hover:bg-white">
                              <input
                                type="checkbox"
                                checked={wsSelectedBatches.includes(b.id)}
                                onChange={(e) => setWsSelectedBatches(prev =>
                                  e.target.checked
                                    ? (prev.includes(b.id) ? prev : [...prev, b.id])
                                    : prev.filter(id => id !== b.id)
                                )}
                                className="mr-2 rounded border-gray-300"
                              />
                              <span className="text-sm text-gray-800">
                                {currentUser?.role === 'school_teacher' ? `Class ${b.name}` : b.name} ({b.student_count} students)
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-white flex-shrink-0">
              <div className="text-sm text-gray-500">Assign this worksheet directly from the syllabus.</div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setInternalWorksheetModal(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignInternalWorksheet}
                  disabled={assigningWorksheet}
                  className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-60 font-medium shadow"
                >
                  {assigningWorksheet ? 'Assigning...' : 'Assign Task'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {analysisLoading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg px-6 py-4 text-sm text-gray-600 shadow-xl">Loading analysis…</div>
        </div>
      )}

      {showFullAnalysis && fullAnalysisData && (
        <FullAnalysisModal
          analysis={fullAnalysisData}
          onClose={() => { setShowFullAnalysis(false); setFullAnalysisData(null); }}
        />
      )}

      {showCompare && compareData && (
        <CompareAnswersModal
          comparisonData={compareData}
          onClose={() => { setShowCompare(false); setCompareData(null); }}
        />
      )}
    </div>
  );
};


export default ViewSyllabusModal;
